import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles } from "lucide-react";

/**
 * Mobile-only sticky Diagnose bubble.
 * Fades in after 30 seconds of scroll activity, links to /diagnose,
 * dismissible for the session.
 * Hidden on: /diagnose, /diagnosis, /diagnosis-pro/*, /story, /grow.
 */
const HIDE_ON_PATHS = [
  "/diagnose",
  "/diagnosis",
  "/diagnosis-pro",
  "/story",
  "/grow",
];

const DELAY_MS = 30000;
const SS_KEY = "gvg_diag_bubble_dismissed";

export default function DiagnoseBubble() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SS_KEY) === "1") {
        setDismissed(true);
        return undefined;
      }
    } catch (_) {
      // ignore
    }

    let scrolled = false;
    const onScroll = () => {
      if (!scrolled && window.scrollY > 200) {
        scrolled = true;
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    const t = setTimeout(() => {
      if (scrolled || window.scrollY > 200) setVisible(true);
    }, DELAY_MS);

    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname]);

  const hide = HIDE_ON_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(SS_KEY, "1");
    } catch (_) {
      // ignore
    }
  };

  if (hide || dismissed) return null;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 60 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="lg:hidden fixed bottom-5 left-4 right-24 z-[55]"
          data-testid="diagnose-bubble"
        >
          <div className="relative rounded-full bg-ink text-bg shadow-[0_20px_50px_-15px_rgba(0,0,0,0.55)] flex items-center pl-5 pr-3 py-3 gap-3">
            <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-brass/20 text-brass shrink-0">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-sans text-[11px] uppercase tracking-mega text-brass leading-none">
                Free · 60-second check
              </p>
              <p className="mt-1 font-serif text-base leading-tight truncate">
                Diagnose your business.
              </p>
            </div>
            <Link
              to="/diagnose"
              data-testid="diagnose-bubble-cta"
              className="rounded-full bg-brass px-4 py-2 font-sans text-[11px] uppercase tracking-mega text-ink hover:bg-bg transition-colors shrink-0"
            >
              Start
            </Link>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              data-testid="diagnose-bubble-dismiss"
              className="ml-1 rounded-full h-7 w-7 flex items-center justify-center text-bg/60 hover:text-bg hover:bg-bg/10 transition-colors shrink-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
